import { useState } from 'react';
import { BrandMark } from '../components/BrandMark';
import type { FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '../api';
import { Callout, Input } from '../components/primitives';
import { ErrorNotice, Field, PasswordInput, ThemeToggle } from '../components/ui';

/** ResetPasswordPage handles an emailed reset-password link
 * (/reset-password?token=... or /set-password?token=... for a
 * Stripe-provisioned owner's first password — the backend endpoint and
 * this form are identical either way). */
export function ResetPasswordPage({ isInitialSetup = false }: { isInitialSetup?: boolean }) {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') ?? '';
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (password !== confirm) {
      setError(new Error('Passwords do not match.'));
      return;
    }
    if (password.length < 10) {
      setError(new Error('Password must be at least 10 characters.'));
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await api.resetPassword(token, password);
      navigate('/app', { replace: true });
    } catch (cause) {
      setError(cause instanceof Error ? cause : new Error(String(cause)));
    } finally {
      setSubmitting(false);
    }
  }

  const isTokenError = error?.message?.includes('expired') || error?.message?.includes('invalid');

  return (
    <div className="auth-shell">
      <div className="auth-shell__theme">
        <ThemeToggle />
      </div>
      <div className="auth-card">
        <Link to="/" className="auth-card__brand">
          <BrandMark size={26} />
          Commerce Ops
        </Link>
        {!token ? (
          <div>
            <h1>Invalid link</h1>
            <p className="muted">This password reset link is missing its verification token.</p>
            <div className="auth-card__actions">
              <Link className="button" to="/forgot-password">
                Request new link
              </Link>
              <Link className="button button--secondary" to="/login">
                Back to sign in
              </Link>
            </div>
          </div>
        ) : (
          <form noValidate className="form" onSubmit={handleSubmit}>
            <h1>{isInitialSetup ? 'Set your password' : 'Reset your password'}</h1>
            <Field label="New password" hint="At least 10 characters.">
              <PasswordInput
                required
                minLength={10}
                autoComplete="new-password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
              />
            </Field>
            <Field label="Confirm password">
              <PasswordInput
                required
                minLength={10}
                autoComplete="new-password"
                value={confirm}
                onChange={(event) => setConfirm(event.target.value)}
              />
            </Field>
            <ErrorNotice error={error} />
            {isTokenError ? (
              <div className="auth-card__actions">
                <Link className="button button--secondary" to="/forgot-password">
                  Request new reset link
                </Link>
                <Link className="button button--secondary" to="/login">
                  Back to sign in
                </Link>
              </div>
            ) : null}
            <button className="button" type="submit" disabled={submitting || password.length < 10 || !confirm}>
              {submitting ? 'Saving' : isInitialSetup ? 'Set password and sign in' : 'Reset password and sign in'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

/** RequestPasswordResetPage is the "forgot password" entry point — email in,
 * a generic "if that address exists, we sent a link" response out, never
 * revealing whether the address is registered. */
export function RequestPasswordResetPage() {
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      await api.requestPasswordReset(email.trim());
      setSent(true);
    } catch (cause) {
      setError(cause instanceof Error ? cause : new Error(String(cause)));
    } finally {
      setSubmitting(false);
    }
  }

  const isEmailNotConfigured =
    error?.message?.includes('email delivery is not configured') ||
    error?.message?.includes('email_not_configured');

  return (
    <div className="auth-shell">
      <div className="auth-shell__theme">
        <ThemeToggle />
      </div>
      <div className="auth-card">
        <Link to="/" className="auth-card__brand">
          <BrandMark size={26} />
          Commerce Ops
        </Link>
        {sent ? (
          <>
            <h1>Check your email</h1>
            <p className="muted">If an account exists for {email}, a reset link is on its way.</p>
            <div className="auth-card__actions">
              <Link className="button button--secondary" to="/login">
                Back to sign in
              </Link>
            </div>
          </>
        ) : (
          <form className="form" onSubmit={handleSubmit}>
            <h1>Reset your password</h1>
            <Field label="Email">
              <Input
                required
                type="email"
                autoComplete="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
              />
            </Field>
            {isEmailNotConfigured ? (
              <Callout tone="info" title="Email delivery is not set up">
                Outbound email is not configured for this installation. Ask your administrator to configure SMTP.
              </Callout>
            ) : (
              <ErrorNotice error={error} />
            )}
            <button className="button" type="submit" disabled={submitting || !email.trim()}>
              {submitting ? 'Sending' : 'Send reset link'}
            </button>
            <p className="muted">
              <Link to="/login">Back to sign in</Link>
            </p>
          </form>
        )}
      </div>
    </div>
  );
}
